import { ChevronDown, Plus } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./base/dropdown-menu";
import { Button } from "./button";
import { CountBadge } from "./count-badge";

type AddMenuItem = {
  label: string;
  /** Relative route to navigate to, e.g. `"add-echo"`. */
  href?: string;
  onClick?: () => void;
};

type AddButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "variant" | "className"
> & {
  /** Relative route to navigate to on click, e.g. `"add"`. */
  href?: string;
  /**
   * When provided, the button opens a dropdown of these options instead of
   * triggering a single action. Use when a table needs several add actions
   * that would otherwise crowd the filter toolbar.
   */
  items?: AddMenuItem[];
  /**
   * Count pill in the button's top-left corner, for pending work the button
   * reopens, e.g. the unsaved rows of a hidden dialog. Values `<= 0` render
   * nothing. Give the button a `title` naming what the count means — the `items`
   * variant drops `title`, so avoid the badge there.
   */
  badge?: number;
};

/**
 * Purple "add" styling shared by the button and menu variants. Same
 * `--action-primary` fill as the header's add-patient button; the border matches
 * the fill, so in dark it does not ring a plum button in magenta-300.
 * `brightness-110` rather than the tokens' own `-hover`: it lightens the fill,
 * where `--action-primary-hover` in light is a step *down* the ramp.
 */
const addButtonClassName =
  "relative bg-action-primary text-action-primary-foreground border border-action-primary hover:brightness-110 hover:bg-action-primary hover:text-action-primary-foreground text-sm font-semibold py-2 px-4 rounded-lg shadow-sm";

/**
 * Purple "add" button with a leading plus icon. Intended for a table's
 * `filterActions`.
 *
 * - Single action: use `onClick` to trigger a dialog or `href` to navigate to a
 *   relative route, e.g. `<AddButton href="add">New entry</AddButton>`.
 * - Multiple actions: pass `items` to render a dropdown menu instead.
 */
export function AddButton({
  children,
  href,
  items,
  onClick,
  badge,
  ...props
}: AddButtonProps) {
  const navigate = useNavigate();

  if (items) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          className={`${addButtonClassName} inline-flex h-8 items-center justify-center gap-1.5`}
        >
          <Plus size={14} />
          {children}
          <ChevronDown size={14} />
          <CountBadge count={badge} />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {items.map((item) => (
            <DropdownMenuItem
              key={item.label}
              onSelect={() => {
                if (item.href) void navigate(item.href);
                item.onClick?.();
              }}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const handleClick = href
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        void navigate(href);
        void onClick?.(e);
      }
    : onClick;

  return (
    <Button {...props} onClick={handleClick} className={addButtonClassName}>
      <Plus size={14} />
      {children}
      <CountBadge count={badge} />
    </Button>
  );
}
